import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import SEO, { serviceSchema } from '../../components/SEO';
import {
  ArrowRight,
  Sparkles,
  Palette,
  Globe2,
  Users,
  Wallet,
  LayoutDashboard,
  Layers,
  ShieldCheck,
  Camera,
  Zap,
  Building2,
  Heart,
  GraduationCap,
  PartyPopper,
  Briefcase,
  BadgeCheck,
  Check,
  Infinity as InfinityIcon,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { heroMosaic, phoneMockups } from '../../mock';
import { getLangFromPath, localizePath } from '../../i18n';

/* -------- Whitelabel phone preview -------- */
function WhitelabelPhone({ brand }) {
  return (
    <div className="relative mx-auto w-[240px] md:w-[260px]">
      <div className="relative aspect-[9/19] rounded-[2.5rem] bg-gradient-to-b from-[#1a1a24] to-[#0a0a0f] p-2 border border-white/10 shadow-2xl shadow-black/70">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-30" />
        <div className="relative w-full h-full rounded-[2rem] overflow-hidden" style={{ backgroundColor: brand.bg }}>
          {/* Status bar */}
          <div className="absolute top-0 left-0 right-0 z-20 px-6 pt-3 flex items-center justify-between text-[10px] font-medium" style={{ color: brand.fg }}>
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1.5 rounded-sm" style={{ backgroundColor: brand.fg }} />
              <div className="w-4 h-2 rounded-sm border" style={{ borderColor: brand.fg + '80' }} />
            </div>
          </div>

          {/* Brand header */}
          <div className="absolute top-10 left-0 right-0 px-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-[10px]" style={{ backgroundColor: brand.accent, color: brand.bg }}>
              {brand.badge}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold leading-tight" style={{ color: brand.fg }}>{brand.name}</div>
              <div className="text-[9px] leading-tight" style={{ color: brand.fg + '80' }}>{brand.subline}</div>
            </div>
          </div>

          {/* Featured photo */}
          <div className="absolute top-24 left-3 right-3 h-[38%] rounded-xl overflow-hidden">
            <img src={brand.hero} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-2 left-2 text-[9px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: brand.accent, color: brand.bg }}>
              24 fotos tuyas
            </div>
          </div>

          {/* Thumb grid */}
          <div className="absolute top-[62%] left-3 right-3 grid grid-cols-3 gap-1">
            {brand.thumbs.map((t, i) => (
              <div key={i} className="aspect-square rounded-md overflow-hidden">
                <img src={t} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="absolute bottom-4 left-3 right-3 h-9 rounded-full flex items-center justify-center text-[10px] font-semibold" style={{ backgroundColor: brand.accent, color: brand.bg }}>
            Descargar mi álbum
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------- Multi-event dashboard mockup -------- */
function MultiEventDashboard() {
  const events = [
    { name: 'Casamiento Ana & Tomás', date: '15/03', photos: 842, revenue: '$ 480k', status: 'Activo', color: 'bg-emerald-500' },
    { name: 'Quinceañera Sofía', date: '02/03', photos: 512, revenue: '$ 210k', status: 'Activo', color: 'bg-emerald-500' },
    { name: 'Corporate SAP Now', date: '28/02', photos: 1284, revenue: '$ 1.2M', status: 'Entregado', color: 'bg-blue-500' },
    { name: 'Graduación LUISS', date: '20/02', photos: 968, revenue: '$ 640k', status: 'Entregado', color: 'bg-blue-500' },
    { name: 'Recital Mateo Sujatovich', date: '10/02', photos: 3421, revenue: '$ 2.1M', status: 'Cerrado', color: 'bg-white/30' },
  ];

  return (
    <div className="relative w-full">
      <div className="rounded-2xl bg-white text-black p-4 shadow-2xl">
        {/* Fake browser */}
        <div className="flex items-center gap-1 mb-3">
          <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <div className="ml-3 text-[9px] text-black/50">fotos.tuestudio.com / eventos</div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold text-black">Mis eventos</div>
          <div className="flex items-center gap-2">
            <div className="px-2 py-1 rounded-md bg-black/5 text-[9px] text-black/60">2026</div>
            <div className="px-2 py-1 rounded-md bg-black text-white text-[9px] font-semibold">+ Nuevo</div>
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="text-[9px] text-emerald-700">Eventos activos</div>
            <div className="text-lg font-display font-bold text-emerald-900">12</div>
          </div>
          <div className="p-2 rounded-lg bg-blue-50 border border-blue-200">
            <div className="text-[9px] text-blue-700">Fotos este mes</div>
            <div className="text-lg font-display font-bold text-blue-900">7.048</div>
          </div>
          <div className="p-2 rounded-lg bg-violet-50 border border-violet-200">
            <div className="text-[9px] text-violet-700">Facturado (100%)</div>
            <div className="text-lg font-display font-bold text-violet-900">$ 4.6M</div>
          </div>
        </div>

        {/* Events list */}
        <div className="space-y-1.5">
          {events.map((e) => (
            <div key={e.name} className="flex items-center gap-3 p-2 rounded-lg border border-black/8 hover:border-black/20 transition-colors">
              <div className={`w-2 h-2 rounded-full ${e.color} shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-black truncate">{e.name}</div>
                <div className="text-[9px] text-black/50">{e.date} · {e.photos} fotos</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-bold text-black">{e.revenue}</div>
                <div className="text-[9px] text-black/50">{e.status}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating badges */}
      <div className="absolute -top-3 -left-3 px-2 py-1 rounded-full bg-white border border-black/8 text-[10px] font-semibold text-black shadow-lg flex items-center gap-1">
        <Wallet className="w-3 h-3 text-emerald-500" /> <span>100% para vos</span>
      </div>
      <div className="absolute -bottom-3 -right-3 px-2 py-1 rounded-full bg-white border border-black/8 text-[10px] font-semibold text-black shadow-lg flex items-center gap-1">
        <Layers className="w-3 h-3 text-violet-500" /> <span>Eventos ilimitados</span>
      </div>
    </div>
  );
}

export default function Productoras() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const to = (p) => localizePath(p, lang);
  const trustBadges = t('services.productoras.trustBadges', { returnObjects: true }) || [];
  const dashboardItems = t('services.productoras.dashboard.items', { returnObjects: true }) || [];
  const useCaseItems = t('services.productoras.useCases.items', { returnObjects: true }) || [];
  const howSteps = t('services.productoras.howItWorks.steps', { returnObjects: true }) || [];
  const directItems = t('services.productoras.comparison.direct.items', { returnObjects: true }) || [];
  const whitelabelItems = t('services.productoras.comparison.whitelabel.items', { returnObjects: true }) || [];
  const securityItems = t('services.productoras.security.items', { returnObjects: true }) || [];
  const brandExamples = [
    {
      key: 'wedding',
      bg: '#f5efe6',
      fg: '#3a2d1f',
      accent: '#c9a26d',
      badge: 'A&T',
      name: 'Ana & Tomás',
      subline: 'Casamiento · 15.03.26',
      hero: heroMosaic.left[1].src,
      thumbs: [phoneMockups.album1, phoneMockups.album2, phoneMockups.album3, phoneMockups.personA, phoneMockups.album4, phoneMockups.album5],
    },
    {
      key: 'studio',
      bg: '#0a0a0f',
      fg: '#ffffff',
      accent: '#f97316',
      badge: 'LP',
      name: 'La Piedra Estudio',
      subline: 'Corporate SAP Now',
      hero: heroMosaic.left[2].src,
      thumbs: [phoneMockups.album1, phoneMockups.album2, phoneMockups.album3, phoneMockups.album4, phoneMockups.album5, phoneMockups.album6],
    },
    {
      key: 'school',
      bg: '#0d1b3a',
      fg: '#ffffff',
      accent: '#38bdf8',
      badge: 'LU',
      name: 'LUISS Graduación',
      subline: 'Facultad de Economía',
      hero: heroMosaic.right[0].src,
      thumbs: [phoneMockups.personA, phoneMockups.album1, phoneMockups.album2, phoneMockups.album3, phoneMockups.album4, phoneMockups.album6],
    },
  ];

  return (
    <>
      <SEO
        title={t('services.productoras.seoTitle')}
        description={t('services.productoras.seoDescription')}
        path="/servicios/productoras"
        schema={serviceSchema({ name: 'Plataforma Whitelabel para Productoras y Estudios de Fotografía', description: 'Solución SaaS whitelabel para productoras, estudios de fotografía y agencias que quieren ofrecer momentIA como su producto propio.', path: '/servicios/productoras' })}
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 lg:pt-20 pb-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 -left-32 w-[500px] h-[500px] bg-orange-500/12 rounded-full blur-[140px]" />
          <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-violet-500/15 rounded-full blur-[160px]" />
        </div>
        <div className="container-mi relative">
          <div className="max-w-3xl">
            <Link to={to('/')} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-300 mb-5 hover:text-orange-200 transition-colors">
              <span>{t('services.productoras.breadcrumb')}</span><span>/</span><span className="text-white/60">{t('services.productoras.breadcrumbCurrent')}</span>
            </Link>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.02]">
              {t('services.productoras.heroTitle1')}
              <span className="block bg-gradient-to-r from-orange-300 via-amber-300 to-yellow-300 bg-clip-text text-transparent">
                {t('services.productoras.heroTitleHighlight')}
              </span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
              {t('services.productoras.heroSubtitle')}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to={to('/request-demo')}>
                <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-6 rounded-full font-semibold" data-testid="productoras-cta-demo">
                  {t('services.productoras.ctaDemo')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to={to('/precios')}>
                <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                  {t('services.productoras.ctaPricing')}
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="mt-10 flex flex-wrap gap-3">
              {[Wallet, InfinityIcon, Palette, Zap].map((Icon, i) => (
                <div key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 text-sm text-white/80">
                  <Icon className="w-3.5 h-3.5 text-orange-300" />
                  {trustBadges[i]}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHITELABEL PHONES */}
      <section className="py-20 lg:py-24 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-300 mb-3 flex items-center gap-2">
              <Palette className="w-3.5 h-3.5" /> {t('services.productoras.whitelabel.eyebrow')}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.productoras.whitelabel.titleLine1')}
              <span className="block text-white/50">{t('services.productoras.whitelabel.titleLine2')}</span>
            </h2>
            <p className="mt-5 text-white/70 max-w-xl">
              {t('services.productoras.whitelabel.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {brandExamples.map((b) => (
              <div key={b.key} className="flex flex-col items-center">
                <WhitelabelPhone brand={b} />
                <div className="mt-6 text-center">
                  <div className="text-sm font-semibold text-white">{b.name}</div>
                  <div className="text-xs text-white/50">{b.subline}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MULTI-EVENT DASHBOARD */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-orange-300 mb-3 flex items-center gap-2">
                <LayoutDashboard className="w-3.5 h-3.5" /> {t('services.productoras.dashboard.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.productoras.dashboard.titleLine1')}
                <br />
                <span className="bg-gradient-to-r from-orange-300 to-amber-300 bg-clip-text text-transparent">{t('services.productoras.dashboard.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.productoras.dashboard.description')}
              </p>
              <div className="mt-8 space-y-3">
                {[Layers, Users, Wallet, Camera].map((Icon, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#111116] border border-white/8">
                    <div className="w-9 h-9 rounded-lg bg-orange-500/10 border border-orange-400/25 flex items-center justify-center text-orange-300 shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">{dashboardItems[i]?.title}</div>
                      <div className="text-xs text-white/60 mt-0.5">{dashboardItems[i]?.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <MultiEventDashboard />
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-300 mb-3">{t('services.productoras.useCases.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.productoras.useCases.titleLine1')}
              <br />
              {t('services.productoras.useCases.titleLine2')}
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[Heart, PartyPopper, Briefcase, GraduationCap, Building2, Camera].map((Icon, i) => (
              <div key={i} className="card-lift p-6 rounded-2xl bg-[#111116] border border-white/8">
                <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-400/25 flex items-center justify-center text-orange-300 mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-display text-lg font-semibold text-white">{useCaseItems[i]?.title}</div>
                <div className="text-sm text-white/60 mt-1">{useCaseItems[i]?.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS - 4 STEPS */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-300 mb-3 flex items-center gap-2">
              <Zap className="w-3.5 h-3.5" /> {t('services.productoras.howItWorks.eyebrow')}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.productoras.howItWorks.titleLine1')}
              <br />
              <span className="text-white/50">{t('services.productoras.howItWorks.titleLine2')}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {[BadgeCheck, Palette, Camera, Wallet].map((Icon, i) => (
              <div key={i} className="rounded-2xl bg-[#111116] border border-white/8 p-6 relative">
                <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                  {i + 1}
                </div>
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-400/25 flex items-center justify-center text-orange-300 mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-display text-lg font-semibold text-white mb-2">{howSteps[i]?.title}</div>
                <div className="text-sm text-white/60 leading-relaxed">{howSteps[i]?.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON: momentia direct vs whitelabel */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-300 mb-3">{t('services.productoras.comparison.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.productoras.comparison.titleLine1')}
              <br />
              <span className="bg-gradient-to-r from-orange-300 to-amber-300 bg-clip-text text-transparent">{t('services.productoras.comparison.titleHighlight')}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Direct */}
            <div className="rounded-3xl bg-[#111116] border border-white/10 p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-violet-400/25 bg-violet-500/10 text-xs text-violet-200 mb-4">
                <Sparkles className="w-3 h-3" /> {t('services.productoras.comparison.direct.badge')}
              </div>
              <h3 className="font-display text-2xl font-bold mb-3">{t('services.productoras.comparison.direct.title')}</h3>
              <p className="text-white/70 text-sm mb-6 leading-relaxed">
                {t('services.productoras.comparison.direct.description')}
              </p>
              <ul className="space-y-2 text-sm text-white/80">
                {directItems.map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" /> {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Whitelabel */}
            <div className="rounded-3xl bg-gradient-to-br from-orange-500/15 via-amber-500/8 to-transparent border border-orange-400/25 p-8 relative">
              <div className="absolute top-6 right-6 px-3 py-1 rounded-full bg-gradient-to-r from-orange-400 to-amber-500 text-slate-900 text-[10px] font-black uppercase tracking-wider">
                {t('services.productoras.comparison.whitelabel.premium')}
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-orange-400/25 bg-orange-500/10 text-xs text-orange-200 mb-4">
                <Palette className="w-3 h-3" /> {t('services.productoras.comparison.whitelabel.badge')}
              </div>
              <h3 className="font-display text-2xl font-bold mb-3">{t('services.productoras.comparison.whitelabel.title')}</h3>
              <p className="text-white/70 text-sm mb-6 leading-relaxed">
                {t('services.productoras.comparison.whitelabel.description')}
              </p>
              <ul className="space-y-2 text-sm text-white/80">
                {whitelabelItems.map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-400" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-orange-300 mb-3 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" /> {t('services.productoras.security.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.productoras.security.titleLine1')}
                <br />
                <span className="text-white/50">{t('services.productoras.security.titleLine2')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.productoras.security.description')}
              </p>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {[ShieldCheck, Globe2, Wallet, Zap].map((Icon, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#111116] border border-white/8">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/15 border border-orange-400/25 flex items-center justify-center text-orange-300">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="text-sm text-white/80">{securityItems[i]}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
                <img src={heroMosaic.left[0].src} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
                <img src={heroMosaic.left[1].src} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
                <img src={heroMosaic.right[0].src} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden border border-white/10 relative">
                <img src={heroMosaic.left[3].src} alt="" className="w-full h-full object-cover opacity-40" />
                <div className="absolute inset-0 flex items-center justify-center text-white font-display font-bold text-3xl">
                  +200k
                </div>
                <div className="absolute bottom-3 left-3 text-[10px] text-white/60 uppercase tracking-wider">{t('services.productoras.security.delivered')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-orange-400/20 bg-gradient-to-br from-[#231810] via-[#141018] to-[#0e0e14] p-10 md:p-14 text-center">
            <div className="absolute -top-32 -right-24 w-[500px] h-[500px] bg-orange-500/15 rounded-full blur-[100px]" />
            <div className="absolute -bottom-32 -left-24 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-[100px]" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-orange-400/25 bg-orange-500/10 text-xs text-orange-200 mb-6">
                <Sparkles className="w-3 h-3" /> {t('services.productoras.finalCta.badge')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
                {t('services.productoras.finalCta.title')}
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">
                {t('services.productoras.finalCta.subtitle')}
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Link to={to('/request-demo')}>
                  <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold" data-testid="productoras-cta-bottom">
                    {t('services.productoras.finalCta.ctaDemo')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={to('/precios')}>
                  <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('services.productoras.finalCta.ctaPricing')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
